import { useEffect, useMemo, useRef, useState, useContext } from "react";

import UniversalButton from "components/uiComponents/UniversalButton";
import UniversalInput from "components/uiComponents/UniversalInput";
import { FormulaTree } from "utils/services";

import { ReactComponent as CloseIcon } from "static/icons/close-or.svg";

import { useDispatch, useSelector } from "react-redux";
import {
  createColumnSchemeAC,
  setFunctionValueAC,
  setNewColumnNameValueAC,
  setNodeListAC,
  setTreeAC,
} from "store/actions/condition";
import "./ConditionModal.scss";

import { ReactComponent as DeleteIcon } from "static/icons/trash.svg";
import { useCallback } from "react";
import TabComponent from "components/uiComponents/Tabs";
import FormulaInputWithSuggestionMode from "./FormulaInputWithSuggestionMode/FormulaInputWithSuggestionMode";
import SimpleFormulaInputMode from "./SimpleFormulaInputMode/SimpleFormulaInputMode";
import { conditionTabId } from "utils/types";


const ConditionModal = (props) => {
  const [currentTabId, setCurrentTabId] = useState(conditionTabId.advance);

  const [isValidExpression, setIsValidExpression] = useState(true);

  useEffect(() => {
    const overflow = document.querySelector(".modal_wrap");

    const stopOutsideClick = (event) => {
      if (event.target.classList.contains("modal_wrap")) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    overflow?.addEventListener("click", stopOutsideClick);
  }, []);

  const tabsArray = useMemo(() => {
    return [
      {
        id: conditionTabId.advance,
        text: "Basic",
      },
      {
        id: conditionTabId.basic,
        text: "Custom",
      },
    ];
  }, []);

  const dispatch = useDispatch();
  const newColumnName = useSelector((state) => state.condition.newColumnName);

  const variablesForCondition = useSelector(
    (state) => state.condition.conditionVariables
  );
  const currentTree = useSelector((state) => state.condition.currentTree);
  const functionValue = useSelector((state) => state.condition.functionValue);

  const searchTreeService = useRef(
    new FormulaTree((newTree, newNodeList) => {
      dispatch(setTreeAC(newTree));
      dispatch(setNodeListAC(newNodeList));
    })
  ).current;

  useEffect(() => {
    if (props?.columnName) {
      setColumnNameHandler(props?.columnName);
    }
  }, [props.columnName, dispatch]);

  useEffect(() => {
    if (props.currentTabId) {
      setCurrentTabId(props?.currentTabId);
    }
  }, [props.currentTabId, dispatch]);

  useEffect(() => {
    dispatch(setFunctionValueAC(props?.functionValue));
  }, [props.functionValue, dispatch]);

  useEffect(() => {
    if (props.tree) {
      dispatch(setTreeAC(props.tree));
      searchTreeService.setNewTree(props.tree);
    }
  }, [props.tree, searchTreeService, dispatch]);

  const setColumnNameHandler = useCallback(
    (value) => {
      dispatch(setNewColumnNameValueAC(value));
    },
    [dispatch]
  );

  const onDeleteHandler = () => {
      clearPopUpHandler();
      props.onClose()
      props.deleteCallback(props?.columnName)
  };

  const clearTreeHandler = useCallback(() => {
    dispatch(setTreeAC(null));
    dispatch(setNodeListAC([]));
    searchTreeService.clearTree();
    setIsValidExpression(true)
  }, [dispatch, searchTreeService]);

  const clearPopUpHandler = () => {
    clearTreeHandler();
    dispatch(setNewColumnNameValueAC(""));
    dispatch(setFunctionValueAC(""));
    props.onClose();
  };

  const onTabChangeHandler = (currentTabId) => {
    setCurrentTabId(currentTabId);
  };

  const onApplyHandler = () => {
    const isValidExpression = currentTabId === conditionTabId.advance ? searchTreeService.isValidTree(currentTree) : true;
    
    if (!isValidExpression) {
      setIsValidExpression(false);
      return;
    }
    const string = searchTreeService.createStringExpression(currentTree);
    dispatch(
      createColumnSchemeAC({
        functionValue: currentTabId === conditionTabId.advance ? string : functionValue,
        variablesForCondition,
        newColumnName,
        currentTabId,
        currentTree,
        currentTableId: props.currentTableId,
        prevColumnName: props.columnName,
      })
    );
    dispatch(setNewColumnNameValueAC(""));
    clearTreeHandler();

    clearPopUpHandler();
  };

  const tabsContentObject = useMemo(() => {
    return {
      [conditionTabId.advance]: (
        <div>
          <FormulaInputWithSuggestionMode
            autoActiveInputs={props.autoActiveInputs}
            clearTreeHandler={clearTreeHandler}
            setIsValidExpression={setIsValidExpression}
            searchTreeService={searchTreeService}
          />
          {!isValidExpression ? (
            <div className="expression-error">Unexpected expression</div>
          ) : null}
        </div>
      ),
      [conditionTabId.basic]: <SimpleFormulaInputMode />,
    };
  }, [clearTreeHandler, searchTreeService, isValidExpression, props.autoActiveInputs]);

  const disabledApplyBtn =
    !newColumnName ||
    (!functionValue && currentTabId === conditionTabId.basic) ||
    (!currentTree && currentTabId === conditionTabId.advance);

  const defaultTabId =
    props?.tree || !functionValue
      ? conditionTabId.advance
      : conditionTabId.basic;

  return (
    <div className="conditional-modal">
      <div className="conditional-modal__container">
        <p className="conditional-modal__title">Add custom column</p>

        <div className="conditional-modal__close" onClick={clearPopUpHandler}>
          <CloseIcon />
        </div>
        <div className="conditional-modal__input">
          <span className="conditional-modal__input-label">Column name</span>
          <UniversalInput
            value={newColumnName}
            onChangeValue={setColumnNameHandler}
          />
        </div>
        <div className="conditional-modal-content-wrapper">
          <TabComponent
            tabsArray={tabsArray}
            tabsContentObject={tabsContentObject}
            defaultId={defaultTabId}
            onSetTab={onTabChangeHandler}
          />
        </div>
        <div className="hint">
          You can use basic math operations and work with dates
          <br />
          Example: Round( [Volume Σ] * 5 )
        </div>
      </div>
      <div className="modal_btns">
        {props?.columnName ? (
          <div className="modal-delete-btn">
            <UniversalButton
              leftIcon={<DeleteIcon />}
              type="button"
              label="Delete"
              size="lg"
              labelColor="blue"
              labelHoverColor="blue"
              backgroundColor="white"
              backgroundHoverColor="blue-light"
              onClick={onDeleteHandler}
            />
          </div>
        ) : null}

        <UniversalButton
          type="button"
          label="Close"
          size="lg"
          labelColor="blue"
          labelHoverColor="blue"
          backgroundColor="white"
          backgroundHoverColor="blue-light"
          onClick={clearPopUpHandler}
        />

        <UniversalButton
          type="button"
          label="Apply"
          isDisabled={disabledApplyBtn}
          size="lg"
          onClick={onApplyHandler}
          labelColor="white"
          labelHoverColor="white"
          backgroundColor="orange"
          backgroundHoverColor="orange-light"
        />
      </div>
    </div>
  );
};

export default ConditionModal;
